/**
 * Schémas Zod — Companies & Contacts.
 */

import { z } from "zod";

import { LIMITS } from "../constants.js";

import {
  DateOnlySchema,
  EmailSchema,
  LocaleSchema,
  PhoneSchema,
  PostalCASchema,
  QueryBooleanSchema,
} from "./common.js";

// -----------------------------------------------------------------------------
// Identification type (Chunk D-NEW-D Phase 1)
// -----------------------------------------------------------------------------
//
// Qualifie le `business_number` selon la juridiction de l'entreprise. Permet
// de valider le format `business_number` selon le type sélectionné (regex
// per type, cf. `validateBusinessNumberForType` ci-dessous).
//
// Décisions PO Phase 1 :
//   - Q1 = A : parité Create + Edit (les deux pages exposent le dropdown).
//   - Q2 = A : default empty (placeholder « Choisir un type ») au mount.
//   - Q3 = A : Zod-only enum (pas de CHECK constraint DB-level — migration
//     0031 commit ADD COLUMN sans CHECK ; tolérance rows legacy NULL).
//   - Q4 = A « avec prudence » : strict pour NEQ/BN/EIN_US/CORP_NUM_CA ;
//     pas de regex pour VAT_EU (27 schémas EU incompatibles, risque faux
//     négatif élevé) ni OTHER (libre).
//   - Q7 = A : aucun backfill — rows existantes restent NULL = legacy.
//
// Hors scope Phase 1 (Phases 2-5 différées) :
//   - Country-driven province/state/postal/labels dynamiques
//   - Fuzzy matching anti-doublon
//   - Extraction PDF prefill
//   - Google Places autocomplete adresse
//   - Refactor lookup canal NEQ scoping sur identification_type IN (...)
//
export const IdentificationTypeSchema = z.enum([
  "NEQ",
  "BN",
  "CORP_NUM_CA",
  "EIN_US",
  "VAT_EU",
  "OTHER",
]);
export type IdentificationType = z.infer<typeof IdentificationTypeSchema>;

/**
 * Regex de validation `business_number` per type (Phase 1).
 *
 * - **NEQ** strict : 10 chiffres exact (Registraire des entreprises QC).
 * - **BN** strict : 9 chiffres + suffixe ARC optionnel `XX9999` (RC0001,
 *   RT0001, RP0001, RR0001 — letter A-Z tolérant pour cas futurs ARC).
 * - **EIN_US** strict : 9 chiffres format `XX-XXXXXXX` (avec ou sans tiret).
 * - **CORP_NUM_CA** modéré : 6-12 chiffres (varie fédéral vs provincial,
 *   numérique-only safe).
 * - **VAT_EU** : pas de regex (27 schémas EU incompatibles — décision PO
 *   « moins strict » Q4 pour éviter faux négatifs).
 * - **OTHER** : pas de regex — broker tape ce qu'il veut.
 *
 * Le helper retourne `null` si `bn` est valide pour `type`, sinon un
 * code d'erreur EN court (le frontend i18n résout l'affichage).
 */
const BUSINESS_NUMBER_REGEX: Partial<Record<IdentificationType, RegExp>> = {
  NEQ: /^\d{10}$/,
  BN: /^\d{9}([A-Z]{2}\d{4})?$/i,
  EIN_US: /^\d{2}-?\d{7}$/,
  CORP_NUM_CA: /^\d{6,12}$/,
  // VAT_EU + OTHER : pas de regex (cf. JSDoc ci-dessus).
};

export function validateBusinessNumberForType(
  bn: string,
  type: IdentificationType,
): "invalid_format" | null {
  const regex = BUSINESS_NUMBER_REGEX[type];
  if (!regex) return null; // VAT_EU / OTHER : tolérant
  return regex.test(bn) ? null : "invalid_format";
}

// -----------------------------------------------------------------------------
// CreateCompanySchema (étendu D-NEW-D Phase 1)
// -----------------------------------------------------------------------------

export const CreateCompanySchema = z.object({
  legal_name: z.string().min(2).max(LIMITS.COMPANY_NAME_MAX),
  business_number: z.string().max(50).optional(),
  /**
   * Type d'identifiant entreprise (Chunk D-NEW-D Phase 1). Optionnel — NULL
   * = legacy non spécifié (rows existantes pre-migration interprétées
   * implicitement comme NEQ pour les rows Quebec historiques). Si fourni,
   * le couple (`identification_type`, `business_number`) est validé par
   * `superRefine` ci-dessous.
   */
  identification_type: IdentificationTypeSchema.optional(),
  trade_name: z.string().max(LIMITS.COMPANY_NAME_MAX).optional(),
  industry: z.string().max(100).optional(),
  founded_year: z.number().int().min(1800).max(new Date().getFullYear()).optional(),

  address_street: z.string().max(200).optional(),
  address_city: z.string().max(100).optional(),
  address_province: z.string().length(2).default("QC"),
  address_postal: PostalCASchema.optional(),
  address_country: z.string().length(2).default("CA"),
  phone: PhoneSchema.optional(),
  fax: PhoneSchema.optional(),
  email: EmailSchema.optional(),
  website: z.string().url().max(255).optional(),

  plan_admin_name: z.string().max(120).optional(),
  plan_admin_email: EmailSchema.optional(),
  language: LocaleSchema,

  employee_count: z.number().int().nonnegative().max(100_000).optional(),
  has_seasonal: z.boolean().optional(),
  has_commission: z.boolean().optional(),
  has_unionized: z.boolean().optional(),
  has_remote_abroad: z.boolean().optional(),
  considers_remote_abroad: z.boolean().optional(),
  family_ties: z.boolean().optional(),
  family_ties_note: z.string().max(LIMITS.NOTE_MAX).optional(),

  has_current_plan: z.boolean().optional(),
  current_insurer: z.string().max(100).optional(),
  current_policy_number: z.string().max(50).optional(),
  current_since_date: DateOnlySchema.optional(),
  current_renewal_date: DateOnlySchema.optional(),

  primary_broker_id: z.string().uuid().optional(),
  notes: z.string().max(LIMITS.NOTE_MAX).optional(),
  tags: z.array(z.string().max(50)).max(20).default([]),
});
export type CreateCompanyInput = z.infer<typeof CreateCompanySchema>;

export const UpdateCompanySchema = CreateCompanySchema.partial().extend({
  // 8.D — D1 : `null` accepté pour détacher le courtier principal.
  // L'API doit traiter `null` comme un effacement explicite (SET col = NULL).
  // `undefined` reste "ne touche pas au champ" (champ omis du PATCH).
  primary_broker_id: z.string().uuid().nullable().optional(),
  // D-NEW-D Phase 1 polish 4 — `null` accepté pour effacer un type
  // d'identifiant pré-existant (broker passe à un mode "non spécifié").
  // `undefined` = champ omis du PATCH (la valeur DB reste inchangée).
  identification_type: IdentificationTypeSchema.nullable().optional(),
  // 9.P.2.R2 post-smoke — autoriser PATCH du status pour le flow Restore UI.
  // Archive reste exposée via `DELETE /v1/companies/:id` (pose status='archived'
  // sans deleted_at depuis le fix archive 9.P.2.R2). Restore = `PATCH
  // /v1/companies/:id { status: 'active' }`. Pas de transition vers d'autres
  // valeurs : enum strict 'active' | 'archived' (les autres statuts éventuels
  // sont gérés par d'autres endpoints dédiés, hors UI broker).
  status: z.enum(["active", "archived"]).optional(),
});
export type UpdateCompanyInput = z.infer<typeof UpdateCompanySchema>;

export const CreateContactSchema = z.object({
  company_id: z.string().uuid(),
  full_name: z.string().min(2).max(120),
  title: z.string().max(100).optional(),
  email: EmailSchema.optional(),
  phone: PhoneSchema.optional(),
  is_primary: z.boolean().default(false),
  notes: z.string().max(LIMITS.NOTE_MAX).optional(),
});
export type CreateContactInput = z.infer<typeof CreateContactSchema>;

export const UpdateContactSchema = CreateContactSchema.partial().omit({ company_id: true });
export type UpdateContactInput = z.infer<typeof UpdateContactSchema>;

// -----------------------------------------------------------------------------
// Filtres listing
// -----------------------------------------------------------------------------
export const CompanyFilterSchema = z.object({
  q: z.string().max(100).optional(),
  status: z.enum(["active", "archived", "all"]).default("active"),
  broker_id: z.string().uuid().optional(),
  industry: z.string().max(100).optional(),
  province: z.string().length(2).optional(),
  has_current_plan: QueryBooleanSchema.optional(),
});
export type CompanyFilter = z.infer<typeof CompanyFilterSchema>;

// -----------------------------------------------------------------------------
// 9.T.2 — Company lookup anti-doublon (`GET /v1/companies/lookup`)
//
// Endpoint API-first qui retourne des candidats company existants rankés par
// confidence selon 3 priorités :
//   (1) business_number exact (avec identification_type optionnel)
//   (2) name+postal exact normalisé (unaccent + suffixes corporatifs strippés)
//   (3) trigram fuzzy legal_name (idx_companies_legal_name_trgm livré 0027)
//
// Anti-doublon Phase 1 : intégration `CompanyPicker` (Step1Company du wizard
// `/projects/new`). CompanyCreatePage anti-doublon = chunk satellite futur.
//
// `limit` borné 1..20 — endpoint lookup ciblé, pas browsing paginé.
// -----------------------------------------------------------------------------

/**
 * Query params pour `GET /v1/companies/lookup`. Validation handler-side
 * exige au moins UN des 3 critères discriminants (legal_name / postal_code /
 * business_number) — sinon `COMPANIES_LOOKUP_QUERY_REQUIRED` 422.
 */
export const CompanyLookupQuerySchema = z.object({
  legal_name: z.string().min(1).max(200).optional(),
  postal_code: z.string().min(1).max(20).optional(),
  business_number: z.string().min(1).max(50).optional(),
  identification_type: IdentificationTypeSchema.optional(),
  /** Borné 1..20 ; coerce car arrive en query string. */
  limit: z.coerce.number().int().min(1).max(20).default(5),
  /** Inclure les companies `status='archived'` (default false). */
  include_archived: QueryBooleanSchema.default(false),
});
export type CompanyLookupQuery = z.infer<typeof CompanyLookupQuerySchema>;

/**
 * Buckets de confiance pour l'affichage broker. Le handler les calcule à
 * partir de la priorité matched + signaux (postal match, similarity). Le
 * frontend lit `match_kind` + `confidence` et applique le bucket via i18n.
 */
export const CompanyLookupMatchKindSchema = z.enum([
  "business_number_exact", // Priorité 1 — confidence 1.0
  "name_postal_exact", // Priorité 2 — confidence 0.95
  "name_trigram", // Priorité 3 — confidence 0.6..0.94 (booster postal)
]);
export type CompanyLookupMatchKind = z.infer<typeof CompanyLookupMatchKindSchema>;

/**
 * Un match returné par lookup. Anti-leak : EXCLUS tenant_id (implicite RLS),
 * created_by, notes, tags, primary_broker_id, phone, email, current_* (info
 * commerciale interne). Garde uniquement les colonnes nécessaires à la
 * décision broker (identification + adresse minimale + status).
 */
export const CompanyLookupMatchSchema = z.object({
  company_id: z.string().uuid(),
  legal_name: z.string(),
  trade_name: z.string().nullable(),
  business_number: z.string().nullable(),
  identification_type: z.string().nullable(),
  address_city: z.string().nullable(),
  address_province: z.string().nullable(),
  address_postal: z.string().nullable(),
  status: z.enum(["active", "archived"]),
  confidence: z.number().min(0).max(1),
  match_kind: CompanyLookupMatchKindSchema,
  match_signals: z.object({
    business_number_match: z.boolean(),
    name_normalized_match: z.boolean(),
    postal_match: z.boolean(),
    /** similarity() trigram 0..1, null si pas matché en priorité 3 */
    name_similarity: z.number().min(0).max(1).nullable(),
  }),
});
export type CompanyLookupMatch = z.infer<typeof CompanyLookupMatchSchema>;

/**
 * Wire response — handler répond `c.json({ok: true, data: this})`.
 * `query` echo back les critères normalisés serveur-side (UPPER/trim) pour
 * que le frontend puisse highlighter dans la pré-fill `/companies/new`.
 */
export const CompanyLookupResponseSchema = z.object({
  matches: z.array(CompanyLookupMatchSchema),
  query: z.object({
    legal_name: z.string().nullable(),
    postal_code: z.string().nullable(),
    business_number: z.string().nullable(),
    identification_type: z.string().nullable(),
  }),
  match_count: z.number().int().nonnegative(),
});
export type CompanyLookupResponse = z.infer<typeof CompanyLookupResponseSchema>;
